import type { ErrorMessage, MsgAck, MsgComplete, MsgDelta, OrderStatusEvent, SessionReady, StatusChange, Typing, WsEnvelope, Action } from "@cos/shared";
import { resolveSessionToken, type WidgetSessionSource } from "./session.js";
import { createWidgetAnalytics, WIDGET_EVENTS } from "./analytics.js";
import { WidgetSocket } from "./ws/client.js";
import { Store, initialWidgetState, type WidgetAttachment, type WidgetMessage } from "./state.js";
import { appendDelta, appendLocalNotice, appendUserMessage, clearUnread, completeMessage, noteIfFresh, readStoredUnread, upsertMessage } from "./applyEnvelope.js";
import { mount } from "./mount.js";
import { trackPageViews, type PageTracker } from "./pageTracking.js";
import { themeToCss } from "./theme/applyTheme.js";
import { App, type ChatMode, type LauncherPosition } from "./components/App.js";
import { canOffer, listenForOpen, rememberDismissal, subscribe, type PushDeps } from "./push.js";

interface BaseChatOptions {
  /** Origin the API/WS live at — e.g. `https://chat.<domain>`, or `http://localhost:3001` in dev. */
  apiUrl: string;
  target?: string;
  mode?: ChatMode;
  entryUrl?: string;
  /** Page-journey tracking on the client's site — a per-bot toggle in PLAN.md's GDPR notes, so it's opt-out here rather than unconditional. Defaults to on. */
  trackPageViews?: boolean;
  /**
   * Path of a service worker the *client's site* hosts (see push.ts). Set,
   * shoppers are offered "notify me when someone replies"; unset, they are
   * never asked — a worker can't be served from our origin onto theirs.
   */
  serviceWorker?: string;
  /**
   * `"hidden"` renders no launcher at all — the host page supplies its own
   * button and drives the widget through the controller (`open`, `toggle`,
   * `getUnread`, `onUnreadChange`). The panel itself is unchanged.
   */
  launcher?: "default" | "hidden";
  /** Fires whenever the unread count or its preview text changes — the hook a custom launcher draws its badge and tooltip from. */
  onUnreadChange?: (unread: UnreadState) => void;
  onEvent?: (event: { type: string; data?: unknown }) => void;
}

export type CreateChatOptions = BaseChatOptions & WidgetSessionSource;

/** What a launcher needs to show "someone wrote to you": a count, and the newest message's opening words. */
export interface UnreadState {
  count: number;
  /** Up to 140 characters of the newest unread message; absent when nothing is unread. */
  teaser?: string;
}

export interface ChatController {
  open: () => void;
  close: () => void;
  toggle: () => void;
  isOpen: () => boolean;
  /** The current unread state — read it once after `createChat` returns, then follow `onUnreadChange`. Unread survives page navigation. */
  getUnread: () => UnreadState;
  reset: () => void;
  destroy: () => void;
}

function randomClientMsgId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `m_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function toWsUrl(apiUrl: string, token: string): string {
  const url = new URL("/api/widget/ws", apiUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("token", token);
  return url.toString();
}

/**
 * The bucket is private, so an image is fetched through the API with the
 * same session token the socket uses. Built per message rather than sent
 * by the server: a URL with a token in it would be baked into the payload
 * and outlive the token's own lifetime.
 */
function attachmentUrl(apiUrl: string, token: string, attachmentId: string): string {
  const url = new URL(`/api/widget/attachments/${attachmentId}`, apiUrl);
  url.searchParams.set("token", token);
  return url.toString();
}

function messageFromHistory(row: SessionReady["messages"][number], toUrl: (id: string) => string): WidgetMessage {
  return {
    id: row.id,
    role: row.role,
    text: row.content ?? "",
    blocks: row.blocks,
    attachments: row.attachments.map((attachment) => ({
      id: attachment.id,
      url: toUrl(attachment.id),
      width: attachment.width ?? undefined,
      height: attachment.height ?? undefined,
    })),
    seq: row.seq,
  };
}

/** `packages/shared`'s widget-facing DTO doesn't type `role` as narrowly as `WidgetMessage` does — every role it can send is one we render. */
function isRenderableRole(role: string): role is WidgetMessage["role"] {
  return role === "user" || role === "assistant" || role === "agent" || role === "system";
}

export function createChat(options: CreateChatOptions): ChatController {
  const target = options.target ? document.querySelector<HTMLElement>(options.target) : document.body;
  if (!target) throw new Error(`createChat: target "${options.target}" not found`);

  const stored = readStoredUnread();
  const store = new Store({ ...initialWidgetState, unreadCount: stored.count, teaser: stored.teaser });
  let position: LauncherPosition = "bottom-right";
  let lastUnread: UnreadState = { count: stored.count, teaser: stored.teaser };
  store.subscribe(() => {
    const { unreadCount, teaser } = store.state;
    if (unreadCount === lastUnread.count && teaser === lastUnread.teaser) return;
    lastUnread = { count: unreadCount, teaser };
    options.onUnreadChange?.({ ...lastUnread });
    emit("unread.change", { ...lastUnread });
  });
  let socket: WidgetSocket | undefined;
  let pageTracker: PageTracker | undefined;
  // Bound to the socket lazily: the frame is queued until the connection is
  // up, so an event raised during the mint still goes out.
  const analytics = createWidgetAnalytics((type, data) => socket?.send(type, data));
  // Held so attachment urls can be built; set once the session is minted.
  let sessionToken: string | undefined;

  const toAttachmentUrl = (attachmentId: string) => (sessionToken ? attachmentUrl(options.apiUrl, sessionToken, attachmentId) : "");

  const push: PushDeps = { apiUrl: options.apiUrl, serviceWorker: options.serviceWorker, token: () => sessionToken };
  let pushOffered = false;
  let pushSynced = false;

  /**
   * Offered at the moments the answer is obviously yes — right after the
   * shopper asked something, or when a person is about to reply — and at
   * most once per page. Never on load: a permission ask before anyone has
   * said a word is the pattern browsers now actively penalise.
   */
  function offerPush(): void {
    if (pushOffered) return;
    pushOffered = true;
    void canOffer(push).then((ok) => {
      if (!ok) return;
      store.set({ pushPrompt: "offer" });
      analytics.track(WIDGET_EVENTS.pushPromptShown);
    });
  }

  function emit(type: string, data?: unknown): void {
    options.onEvent?.({ type, data });
  }

  function handleEnvelope(envelope: WsEnvelope): void {
    emit(envelope.type, envelope.data);

    switch (envelope.type) {
      case "session.ready": {
        const data = envelope.data as SessionReady;
        if (data.token) {
          sessionToken = data.token;
          socket?.setSessionToken(data.token);
        }
        const page = pageTracker?.current();
        if (page) socket?.send("page.view", { url: page.url, title: page.title, referrer: page.referrer, language: page.language, isSpaNav: false });
        mountResult.setThemeCss(themeToCss(data.theme));
        position = data.theme.launcher.position;
        analytics.track(WIDGET_EVENTS.widgetLoaded, {
          mode: options.mode ?? "window",
          launcher_position: position,
          resumed: data.messages.length > 0,
        });
        store.update((state) => ({
          ...state,
          botName: data.bot.name,
          greeting: data.bot.greeting,
          conversationStatus: data.status,
          control: data.control,
          allowImageUploads: data.bot.allowImageUploads,
          messages: data.messages.map((row) => messageFromHistory(row, toAttachmentUrl)),
          lastSeq: data.messages.at(-1)?.seq ?? 0,
          botTyping: false,
        }));
        // Permission granted on an earlier visit: re-register quietly, so
        // this visitor's current browser is the one that gets the buzz.
        if (!pushSynced && typeof Notification !== "undefined" && Notification.permission === "granted") {
          pushSynced = true;
          void subscribe(push, false);
        }
        break;
      }
      case "msg.ack": {
        const data = envelope.data as MsgAck;
        store.update((state) => ({ ...state, lastSeq: Math.max(state.lastSeq, data.seq) }));
        break;
      }
      case "msg.delta": {
        const data = envelope.data as MsgDelta;
        store.update((state) => appendDelta(state, data.messageId, data.delta));
        break;
      }
      case "msg.complete": {
        const data = envelope.data as MsgComplete;
        store.update((state) => noteIfFresh(state, completeMessage(state, data), data.messageId));
        break;
      }
      case "msg.new": {
        const data = envelope.data as SessionReady["messages"][number];
        if (!isRenderableRole(data.role)) break;
        const message = messageFromHistory(data, toAttachmentUrl);
        // A person's reply is the case the badge exists for: the shopper has
        // usually closed the widget and moved on by the time it lands.
        store.update((state) => noteIfFresh(state, upsertMessage(state, message), message.id));
        break;
      }
      case "order.status": {
        const data = envelope.data as OrderStatusEvent;
        // Rendered as a notice rather than mutating the order block: the
        // block is what the shopper agreed to, and rewriting it after the
        // fact would make the transcript disagree with the email.
        store.update((state) =>
          upsertMessage(state, {
            id: `order-status-${data.orderId}-${data.status}`,
            role: "system",
            text: "",
            blocks: [
              {
                type: "notice",
                level: data.status === "failed" ? "error" : "info",
                text:
                  data.status === "submitted"
                    ? "Sent — someone will get back to you by email."
                    : data.status === "failed"
                      ? (data.message ?? "That didn't go through. Please try again in a moment.")
                      : "Confirmed — sending now.",
              },
            ],
          }),
        );
        break;
      }
      case "status.change": {
        const data = envelope.data as StatusChange;
        store.set({ conversationStatus: data.status, control: data.control, botTyping: false });
        // A person is now (or soon) answering — and people, unlike the bot,
        // may answer after the shopper has left the page.
        if (data.control === "human" || data.status === "escalated") offerPush();
        break;
      }
      case "typing": {
        const data = envelope.data as Typing;
        store.set({ agentTyping: data.isTyping });
        break;
      }
      case "error": {
        const data = envelope.data as ErrorMessage;
        store.set({ botTyping: false });
        // Reported as an event rather than only handed to `onEvent`: a
        // rate limit or a provider failure on a client's site is something
        // we have to see without the client telling us about it.
        analytics.track(WIDGET_EVENTS.widgetErrored, { code: data.code, source: "server" });
        emit("error", data);
        break;
      }
      default:
        break;
    }
  }

  /**
   * Uploads happen over HTTP, not the socket: they are large and slow, and
   * a photo halfway up a 3G connection must not block the ping that keeps
   * the conversation alive. The message itself is only sent once every
   * upload has an id, so a caption never arrives without its picture.
   */
  async function uploadImages(files: File[]): Promise<string[]> {
    if (files.length === 0 || !sessionToken) return [];
    const url = new URL("/api/widget/upload", options.apiUrl);
    url.searchParams.set("token", sessionToken);

    const ids: string[] = [];
    for (const file of files) {
      try {
        const response = await fetch(url.toString(), { method: "POST", headers: { "content-type": file.type }, body: file });
        if (!response.ok) {
          analytics.track(WIDGET_EVENTS.widgetErrored, { code: "upload_failed", source: "upload", status: response.status });
          emit("error", { code: "upload_failed", message: `${file.name} could not be sent` });
          continue;
        }
        ids.push(((await response.json()) as { id: string }).id);
      } catch {
        analytics.track(WIDGET_EVENTS.widgetErrored, { code: "upload_failed", source: "upload", status: 0 });
        emit("error", { code: "upload_failed", message: `${file.name} could not be sent` });
      }
    }
    return ids;
  }

  function sendMessage(text: string, files: File[] = []): void {
    const clientMsgId = randomClientMsgId();
    const page = pageTracker?.current();
    // The local object urls the composer already made: the shopper's own
    // photo appears the instant they hit send rather than after the upload
    // and the round trip that follows it.
    const previews: WidgetAttachment[] = files.map((file, index) => ({ id: `${clientMsgId}-${index}`, url: URL.createObjectURL(file) }));
    store.update((state) => appendUserMessage(state, clientMsgId, text, previews.length > 0 ? previews : undefined));

    void (async () => {
      const attachmentIds = await uploadImages(files);

      // Some or all of the photos never made it. Whatever happens next, the
      // shopper is told: a picture that disappears between hitting send and
      // the bot's reply reads as the bot ignoring it, and they have no way
      // to tell that it was the upload and not the answer.
      const failed = files.length - attachmentIds.length;
      if (failed > 0) {
        store.update((state) =>
          appendLocalNotice(state, failed === files.length ? "Your image could not be sent." : `${failed} of your images could not be sent.`),
        );
      }

      // Every upload failed and there is no text to send on its own —
      // sending an empty message would be rejected, so the notice above is
      // the whole of what the shopper gets, and the turn ends here.
      if (files.length > 0 && attachmentIds.length === 0 && text.length === 0) {
        store.set({ botTyping: false });
        return;
      }
      // The page the shopper was on when they hit send. Country is *not* sent
      // — it's resolved server-side, because anything the client sends is
      // something the client can lie about.
      socket?.send("msg.send", { clientMsgId, text, attachmentIds, pageUrl: page?.url, pageTitle: page?.title });
      offerPush();
    })();
  }

  /**
   * A quick-reply chip, which reaches `sendMessage` directly rather than
   * through `handleAction` — the two are separate paths in
   * `render/blocks.ts` (a chip under a message, versus an actions block),
   * and instrumenting only the second would have counted the rarer one.
   */
  function handleQuickReply(value: string): void {
    analytics.track(WIDGET_EVENTS.quickReplyClicked, { source: "chip" });
    sendMessage(value);
  }

  function handleAction(action: Action): void {
    switch (action.intent) {
      case "quick_reply":
        analytics.track(WIDGET_EVENTS.quickReplyClicked, { source: "action" });
        sendMessage(action.value);
        break;
      case "reset_session":
        controller.reset();
        break;
      case "escalate":
        // Escalation is agency-triggered via the `escalate_to_human` tool
        // server-side (M3+); this button just tells the shopper what
        // they clicked. No client-initiated escalation message exists yet.
        analytics.track(WIDGET_EVENTS.escalationClicked);
        emit("action.escalate.clicked");
        break;
      default:
        analytics.track(WIDGET_EVENTS.actionClicked, { intent: action.intent });
        emit("action.clicked", action);
        break;
    }
  }

  const mountResult = mount(
    target,
    (
      <App
        store={store}
        mode={options.mode ?? "window"}
        position={position}
        launcher={options.launcher ?? "default"}
        onSend={sendMessage}
        onNewConversation={() => controller.reset()}
        onToggle={() =>
          store.update((state) => {
            if (state.panelOpen) {
              analytics.track(WIDGET_EVENTS.widgetClosed, { source: "launcher", messages: state.messages.length });
              return { ...state, panelOpen: false };
            }
            // The top of every widget funnel, and the one step no server-side
            // event can stand in for: a shopper who never opens the launcher
            // leaves no trace beyond the session the page load minted.
            analytics.track(WIDGET_EVENTS.widgetOpened, { source: "launcher", unread: state.unreadCount });
            return clearUnread({ ...state, panelOpen: true });
          })
        }
        onQuickReply={handleQuickReply}
        onAction={handleAction}
        onOrderConfirm={(orderId) => socket?.send("order.confirm", { orderId })}
        onFormSubmit={(fields) => socket?.send("form.submit", { fields })}
        onFeedback={(messageId, rating, comment) => socket?.send("feedback.submit", { messageId, rating, comment })}
        onPushAccept={() => {
          store.set({ pushPrompt: "busy" });
          void subscribe(push, true).then((ok) => {
            store.update((state) => {
              const next = { ...state, pushPrompt: undefined };
              return ok ? appendLocalNotice(next, "Notifications on — we'll let you know when someone replies.", "info") : next;
            });
            if (ok) analytics.track(WIDGET_EVENTS.pushEnabled);
          });
        }}
        onPushDismiss={() => {
          rememberDismissal();
          store.set({ pushPrompt: undefined });
          analytics.track(WIDGET_EVENTS.pushPromptDismissed);
        }}
      />
    ),
  );

  const controller: ChatController = {
    open: () => {
      // `source` separates this from the launcher: a client site that drives
      // the widget from its own button would otherwise read as a widget
      // nobody ever opens.
      analytics.track(WIDGET_EVENTS.widgetOpened, { source: "api", unread: store.state.unreadCount });
      store.update((state) => clearUnread({ ...state, panelOpen: true }));
    },
    toggle: () => (store.state.panelOpen ? controller.close() : controller.open()),
    isOpen: () => store.state.panelOpen,
    getUnread: () => ({ ...lastUnread }),
    close: () => {
      analytics.track(WIDGET_EVENTS.widgetClosed, { source: "api", messages: store.state.messages.length });
      store.set({ panelOpen: false });
    },
    reset: () => {
      analytics.track(WIDGET_EVENTS.conversationReset, { messages: store.state.messages.length });
      socket?.send("session.reset", {});
    },
    destroy: () => {
      socket?.close();
      pageTracker?.stop();
      mountResult.unmount();
    },
  };

  // Arriving from a notification: open straight onto the conversation.
  if (options.serviceWorker) listenForOpen(controller.open);

  void (async () => {
    try {
      const mint = () => resolveSessionToken(options, { apiUrl: options.apiUrl, entryUrl: options.entryUrl ?? window.location.href });
      const token = await mint();
      sessionToken = token;
      socket = new WidgetSocket({
        wsUrl: toWsUrl(options.apiUrl, token),
        onEnvelope: handleEnvelope,
        onStateChange: (state) => store.set({ connection: state }),
        getResumeSeq: () => (store.state.lastSeq > 0 ? store.state.lastSeq : undefined),
        // The session token is short-lived (an hour, server-side) and the
        // API assumes the widget re-mints rather than holding one forever —
        // a widget left open on a tab past that point otherwise retries a
        // credential the server has already refused, and never recovers.
        // Kept in sync with `sessionToken` because attachment URLs are
        // built from it per message.
        refreshSessionToken: async () => {
          const fresh = await mint();
          sessionToken = fresh;
          return fresh;
        },
      });
      socket.connect();

      if (options.trackPageViews !== false) {
        // Queued through the socket, which drops sends while it's not open;
        // the first view is re-reported on `session.ready` below so a page
        // load never goes unrecorded just because the socket was still
        // connecting.
        pageTracker = trackPageViews((page, isSpaNav) => {
          socket?.send("page.view", { url: page.url, title: page.title, referrer: page.referrer, language: page.language, isSpaNav });
        });
      }
    } catch (error) {
      // Not relayed: the relay *is* the socket, and there isn't one. This
      // failure is visible server-side instead, as a session mint that
      // never became a connection.
      emit("error", { code: "session_failed", message: error instanceof Error ? error.message : String(error) });
    }
  })();

  return controller;
}

function deriveApiUrl(scriptSrc: string): string {
  const scriptUrl = new URL(scriptSrc);
  const host = scriptUrl.hostname.startsWith("cdn.") ? `chat.${scriptUrl.hostname.slice(4)}` : scriptUrl.hostname;
  return `${scriptUrl.protocol}//${host}`;
}

/**
 * Zero-JS embed: `<script src=".../v1/chat.js" data-public-key="pk_live_…" defer>`.
 * `document.currentScript` is only set for a classic script element being
 * evaluated right now — it's `null` for an ESM import, so this is a no-op
 * when `createChat` is imported programmatically instead.
 */
(function autoInit() {
  const script = document.currentScript as HTMLScriptElement | null;
  const publicKey = script?.dataset.publicKey;
  if (!script || !publicKey) return;

  createChat({
    publicKey,
    apiUrl: script.dataset.apiUrl ?? deriveApiUrl(script.src),
    target: script.dataset.target,
    mode: (script.dataset.mode as ChatMode | undefined) ?? "window",
    serviceWorker: script.dataset.serviceWorker,
    launcher: script.dataset.launcher === "hidden" ? "hidden" : "default",
  });
})();
